import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

export interface CampaignReportObserverEvent {
  id: string;
  campaign_id?: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  total_contacts?: number;
  total_calls?: number;
  stats_hash?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  error_message?: string | null;
  progress?: number;
  processed_contacts?: number;
  processed_calls?: number;
  estimated_seconds_remaining?: number | null;
  progress_message?: string | null;
}

interface UseCampaignReportWebSocketOptions {
  onReportUpdated?: (event: CampaignReportObserverEvent) => void;
  onReportReady?: (event: CampaignReportObserverEvent) => void;
  onReportFailed?: (event: CampaignReportObserverEvent) => void;
}

interface UseCampaignReportWebSocketReturn {
  isConnected: boolean;
  isSubscribed: boolean;
}

/**
 * Subscribe to campaign report stream updates.
 */
export function useCampaignReportWebSocket(
  reportId: string | undefined,
  options: UseCampaignReportWebSocketOptions = {},
): UseCampaignReportWebSocketReturn {
  const { onReportUpdated, onReportReady, onReportFailed } = options;
  const { state, addObserver, removeObserver } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const callbacksRef = useRef<UseCampaignReportWebSocketOptions>({
    onReportUpdated,
    onReportReady,
    onReportFailed,
  });

  useEffect(() => {
    callbacksRef.current = {
      onReportUpdated,
      onReportReady,
      onReportFailed,
    };
  }, [onReportUpdated, onReportReady, onReportFailed]);

  const handleEvent = useCallback(
    (data: CampaignReportObserverEvent, action: string) => {
      if (action !== 'update' && action !== 'create') {
        return;
      }

      if (reportId && data.id && data.id !== reportId) {
        return;
      }

      callbacksRef.current.onReportUpdated?.(data);

      if (data.status === 'completed') {
        callbacksRef.current.onReportReady?.(data);
      } else if (data.status === 'failed') {
        callbacksRef.current.onReportFailed?.(data);
      }
    },
    [reportId],
  );

  useEffect(() => {
    if (!reportId || !state.isConnected) {
      setIsSubscribed(false);
      return;
    }

    const observerId = addObserver(
      'campaign_report',
      { data: { report_id: reportId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
      },
    );
    setIsSubscribed(true);

    return () => {
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [reportId, state.isConnected, addObserver, removeObserver, handleEvent]);

  return {
    isConnected: state.isConnected,
    isSubscribed,
  };
}

export default useCampaignReportWebSocket;
